import LoaderOverlay from "../Modules/LoaderOverlay";

interface Coordinate {
    x: number;
    y: number;
}

class Image360 {
    static SELECTORS = {
        NODE: "section.image-360",
        STAGE: ":scope > .stage",
        IMAGES: ":scope > .stage img"
    };

    static CLASSNAMES = {
        LOADING: "loading"
    };

    private readonly node: HTMLElement;
    private readonly stage: HTMLElement;
    private readonly track: HTMLInputElement;
    private readonly images: HTMLImageElement[];
    private pointerActive = false;
    private pointerStart: Coordinate;
    private stageRect: DOMRect;
    private numTiles: number;
    private switchImageThreshold: number;
    private loaderOverlay: LoaderOverlay;

    constructor(node: HTMLElement) {
        this.node = node;
        this.stage = this.node.querySelector<HTMLElement>(Image360.SELECTORS.STAGE);
        const images = this.node.querySelectorAll("img") as NodeListOf<HTMLImageElement>;
        this.images = Array.from(images);
        this.track = node.querySelector<HTMLInputElement>('input[type="range"]');
        this.node.classList.remove(Image360.CLASSNAMES.LOADING);
        this.loaderOverlay = new LoaderOverlay(this.node).render();
        this.initWait().then(this.init.bind(this));
    }

    /**
     * wait for all images to be loaded
     * @private
     */
    private async initWait() {
        for (const i of this.images) {
            await i.decode();
        }
    }

    /**
     * initialize
     * @private
     */
    private init() {
        this.numTiles = this.node.querySelectorAll(Image360.SELECTORS.IMAGES).length;
        this.initDimensions().addListeners();
        this.loaderOverlay.destroy();
        this.track.disabled = false;
    }

    /**
     * initialize dimensions data
     * @private
     */
    private initDimensions(): Image360 {
        this.updateStageRect();
        this.switchImageThreshold = this.stageRect.width / (this.numTiles + 1);

        return this;
    }

    /**
     * add event listeners
     * @private
     */
    private addListeners(): Image360 {
        const resizeHandler = this.initDimensions.bind(this);
        const startHandler = this.handleStart.bind(this);
        const moveHandler = this.handleMove.bind(this);
        const stopHandler = this.handleStop.bind(this);
        window.addEventListener("resize", resizeHandler);
        this.track.addEventListener("input", this.handleTrackChange.bind(this));
        this.stage.addEventListener("pointerdown", startHandler);
        this.stage.addEventListener("pointermove", moveHandler);
        this.stage.addEventListener("pointerup", stopHandler);
        this.stage.addEventListener("pointerleave", stopHandler);

        return this;
    }

    /**
     * handle track input events
     * @private
     */
    private handleTrackChange() {
        this.node.style.setProperty("--current", `${Math.round(parseInt(this.track.value) / 100)}`);
    }

    /**
     * handle stage pointer down events
     * @param {PointerEvent} e
     * @private
     */
    private handleStart(e: PointerEvent) {
        e.preventDefault();
        this.pointerStart = this.getPointerOffset(e);
        this.pointerActive = true;
    }

    /**
     * handle pointer move events
     * @param {PointerEvent} e
     * @private
     */
    private handleMove(e: PointerEvent): void {
        if (this.pointerActive) {
            e.preventDefault();
            const offset = this.getPointerOffset(e);
            const moved = offset.x - this.pointerStart.x;

            const scrolled = offset.y - this.pointerStart.y;

            if (Math.abs(scrolled) <= Math.abs(moved) && Math.abs(moved) > this.switchImageThreshold) {
                this.stage.classList.add("moving");
                this.pointerStart = offset;
                let next;
                const current = parseInt(this.track.value) / 100;
                if (moved > 0) {
                    next = Math.min(this.images.length, current + 1);
                } else {
                    next = Math.max(0, current - 1);
                }
                this.track.value = (next * 100).toString();
                this.track.dispatchEvent(new Event("input"));
            }
        }
    }

    /**
     * handle stage pointer up/leave events
     * @private
     */
    private handleStop() {
        this.pointerActive = false;
        this.stage.classList.remove("moving");
    }

    /**
     * update cached stage DOMRect object
     * @private
     */
    private updateStageRect() {
        this.stageRect = this.stage.getBoundingClientRect();
    }

    /**
     * compute pointer offset
     * @param {PointerEvent} e
     * @private
     */
    private getPointerOffset(e: PointerEvent): Coordinate {
        return {
            x: e.clientX - this.stageRect.left,
            y: e.clientY - this.stageRect.top
        };
    }
}

const image360 = document.querySelector<HTMLElement>(Image360.SELECTORS.NODE);
if (image360) {
    new Image360(image360);
}
